/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

/**
 * Helper object
 * @constructor
 */

import {ImageManager} from "App/environment/managers/image_manager";
import ManipulatorManager from "App/environment/managers/manipulator_manager";
import {vec3} from "gl-matrix";

export class Tools {

    private static instance: Tools = null;

    private imageManager: ImageManager = new ImageManager();
    private manipulatorManager: ManipulatorManager = new ManipulatorManager();

    /**
     * @type {WebGLTexture}
     * @private
     */
    private _placeholderTextures: {} = {};

    constructor() {
        imageManager: new ImageManager();
        manipulatorManager: new ManipulatorManager();
    }

    public static getInstance(): Tools {
        if (!Tools.instance) {
            Tools.instance = new Tools();
        }
        return Tools.instance;
    }

    public getImageManager(): ImageManager {
        return this.imageManager;
    }

    public getManipulatorManager(): ManipulatorManager {
        return this.manipulatorManager;
    }

    /**
     * @param {WebGLRenderingContext} gl
     * @param {number[]} [color = [255, 255, 255, 255]]
     */
    public getPlaceholderTexture(gl, color): WebGLTexture {
        let color2string = function(c) {
            return c[0] + "." + c[1] + "." + c[2] + "." + c[3];
        };

        if (color === undefined) {
            color = [255, 255, 255, 255];
        }

        let key = color2string(color);

        // Create if not exists
        if (!this._placeholderTextures[key]) {
            this._placeholderTextures[key] = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, this._placeholderTextures[key]);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(color));

            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            gl.bindTexture(gl.TEXTURE_2D, null);
        }

        return this._placeholderTextures[key];
    }

    /**
     * @param {WebGLRenderingContext} gl
     * @param {number[]} vertices
     * @param {boolean} [dynamic=false]
     * @returns {WebGLBuffer}
     */
    public createVBO(gl, vertices, dynamic): WebGLBuffer  {
        dynamic = dynamic === undefined ? false : dynamic;
        let vbo = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), dynamic ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        return vbo;
    }

    /**
     * @param {WebGLRenderingContext} gl
     * @param {number[]} indexes
     * @returns {WebGLBuffer}
     */
    public createEBO(gl, indexes): WebGLBuffer {
        let ebo = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(indexes), gl.STATIC_DRAW);
        return ebo;
    }

    /**
     * Convert indices and vertices to triangles ('decompress')
     *
     * @param {number[]} indices
     * @param {number[]} vertices
     * @return {number[][]}
     */
    public toTriangles(indices, vertices): any[] {
        let il = indices.length;
        let triangles = [];
        let triangle = [];
        let count = 0;

        for (let i = 0; i < il; i++) {
            if (i >= il) {
                break;
            }

            let start = indices[i] * 3;
            let vertex = vec3.fromValues(vertices[start], vertices[start + 1], vertices[start + 2]);
            triangle.push(vertex);

            count++;
            if (count >= 3) {
                triangles.push(triangle);
                count = 0;
                triangle = [];
            }
        }

        return triangles;
    }

    /**
     * Convert indices and vertices to triangles ('decompress')
     *
     * @param {number[]} indices
     * @param {number[]} vertices
     * @return {number[][]}
     */
    public toTrianglesWithMinMax(indices, vertices): any[] {
        let il = indices.length;
        let triangles = [];
        let triangle = [];
        let count = 0;
        let min = vec3.create();
        let max = vec3.create();

        for (let i = 0; i < il; i++) {
            if (i >= il) {
                break;
            }

            let start = indices[i] * 3;
            let vertex = vec3.fromValues(vertices[start], vertices[start + 1], vertices[start + 2]);
            triangle.push(vertex);

            if (i === 0) {
                vec3.copy(min, vertex);
                vec3.copy(max, vertex);
            }

            for (let a = 0; a < 3; a++) {
                if (vertex[a] < min[a]) {
                    min[a] = vertex[a];
                }
                if (vertex[a] > max[a]) {
                    max[a] = vertex[a];
                }
            }

            count++;
            if (count >= 3) {
                triangle.push(min);
                triangle.push(max);
                triangles.push(triangle);
                count = 0;
                triangle = [];
            }
        }

        return triangles;
    }

    /**
     * Point in triangle 2D
     * https://stackoverflow.com/questions/2049582/how-to-determine-if-a-point-is-in-a-2d-triangle
     */
    public inTriangle2D(x1, y1, x2, y2, x3, y3, px, py): boolean {
        let sign = function(a1, a2, b1, b2, c1, c2) {
            return (a1 - c1) * (b2 - c2) - (b1 - c1) * (a2 - c2);
        };

        let b1 = sign(px, py, x1, y1, x2, y2) < 0.0;
        let b2 = sign(px, py, x2, y2, x3, y3) < 0.0;
        let b3 = sign(px, py, x3, y3, x1, y1) < 0.0;

        return ((b1 === b2) && (b2 === b3));
    }
}
